import {
    signIn as amplifySignIn,
    signOut as amplifySignOut,
    confirmSignIn as amplifyConfirmSignIn,
    getCurrentUser as amplifyGetCurrentUser,
    fetchAuthSession,
    resetPassword as amplifyResetPassword,
    confirmResetPassword as amplifyConfirmResetPassword,
    updatePassword as amplifyUpdatePassword,
    setUpTOTP as amplifySetUpTOTP,
    verifyTOTPSetup as amplifyVerifyTOTPSetup,
    updateMFAPreference,
    fetchMFAPreference,
    associateWebAuthnCredential as amplifyAssociateWebAuthn,
    listWebAuthnCredentials as amplifyListWebAuthn,
    deleteWebAuthnCredential as amplifyDeleteWebAuthn,
    updateUserAttribute as amplifyUpdateUserAttribute,
    confirmUserAttribute as amplifyConfirmUserAttribute,
    sendUserAttributeVerificationCode as amplifySendVerificationCode,
    fetchUserAttributes as amplifyFetchUserAttributes,
} from 'aws-amplify/auth';
import type { SignInOutput } from 'aws-amplify/auth';
import { isAwsAuth } from '../amplifyConfig';

export type { SignInOutput };

export const authType: 'aws' | 'azure' = isAwsAuth ? 'aws' : 'azure';

export interface AuthUser {
    email: string;
    sub: string;
}

interface AzureClientPrincipal {
    identityProvider: string;
    userId: string;
    userDetails: string;
    userRoles: string[];
}

export const signIn = async (email?: string, password?: string): Promise<SignInOutput | void> => {
    if (authType === 'azure') {
        window.location.href = '/.auth/login/aad';
        return;
    }

    if (!email || !password) throw new Error('Missing credentials');

    return amplifySignIn({ username: email, password });
};

export const signInWithPasskey = async (email: string): Promise<SignInOutput> => {
    return amplifySignIn({
        username: email,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
    });
};

export const confirmSignIn = async (challengeAnswer: string): Promise<SignInOutput> => {
    return amplifyConfirmSignIn({ challengeResponse: challengeAnswer });
};

export const signOut = async (): Promise<void> => {
    if (authType === 'azure') {
        window.location.href = '/.auth/logout';
        return;
    }

    await amplifySignOut();
};

export const getCurrentUser = async (): Promise<AuthUser | null> => {
    if (authType === 'azure') {
        try {
            const response = await fetch('/.auth/me');
            const payload = await response.json();
            const clientPrincipal = payload.clientPrincipal as AzureClientPrincipal | null;

            if (clientPrincipal) {
                return {
                    email: clientPrincipal.userDetails,
                    sub: clientPrincipal.userId
                };
            }
            return null;
        } catch {
            return null;
        }
    }

    try {
        const user = await amplifyGetCurrentUser();
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email as string ?? user.username;
        return {
            email,
            sub: user.userId,
        };
    } catch {
        return null;
    }
};

export const getIdToken = async (): Promise<string | null> => {
    if (authType === 'azure') return null;
    try {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() ?? null;
    } catch {
        return null;
    }
};

export const getAccessToken = async (): Promise<string | null> => {
    if (authType === 'azure') return null;
    try {
        const session = await fetchAuthSession();
        return session.tokens?.accessToken?.toString() ?? null;
    } catch {
        return null;
    }
};

export const initiatePasswordReset = async (email: string) => {
    return amplifyResetPassword({ username: email });
};

export const completePasswordReset = async (email: string, code: string, newPassword: string) => {
    return amplifyConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
};

export const changePassword = async (oldPassword: string, newPassword: string) => {
    return amplifyUpdatePassword({ oldPassword, newPassword });
};

export const setupTOTP = async () => {
    return amplifySetUpTOTP();
};

export const verifyTOTP = async (code: string) => {
    await amplifyVerifyTOTPSetup({ code });
    await updateMFAPreference({ totp: 'PREFERRED' });
};

export const getMFAPreference = async () => {
    return fetchMFAPreference();
};

export const setMFAPreference = async (preferred: 'totp' | 'sms') => {
    const current = await fetchMFAPreference();
    const hasTotp = current.preferred === 'TOTP' || current.enabled?.includes('TOTP');
    const hasSms = current.preferred === 'SMS' || current.enabled?.includes('SMS');

    if (preferred === 'totp') {
        return updateMFAPreference({ totp: 'PREFERRED', ...(hasSms ? { sms: 'ENABLED' } : {}) });
    }
    return updateMFAPreference({ sms: 'PREFERRED', ...(hasTotp ? { totp: 'ENABLED' } : {}) });
};

export const registerPasskey = async () => {
    return amplifyAssociateWebAuthn();
};

export const listPasskeys = async () => {
    return amplifyListWebAuthn();
};

export const deletePasskey = async (credentialId: string) => {
    return amplifyDeleteWebAuthn({ credentialId });
};

export const getPhoneNumber = async (): Promise<string | null> => {
    try {
        const attrs = await amplifyFetchUserAttributes();
        return attrs.phone_number ?? null;
    } catch {
        return null;
    }
};

export const updatePhoneNumber = async (phoneNumber: string) => {
    const result = await amplifyUpdateUserAttribute({
        userAttribute: { attributeKey: 'phone_number', value: phoneNumber },
    });
    if (result.nextStep.updateAttributeStep !== 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        await amplifySendVerificationCode({ userAttributeKey: 'phone_number' });
    }
};

export const confirmPhoneNumber = async (code: string) => {
    return amplifyConfirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: code,
    });
};
